import { Amenity } from '../../models/amenity.model';
import { amenityColorOf, AmenityFilter, calendarCancelledColor, CalendarColor, CalendarDay, CalendarEvent, CalendarEventState, CalendarHourSlot, CalendarSkeletonSlot, CalendarViewMode, CalendarWeek, StateFilter } from '../../models/calendar.model';
import { Booking } from '../../models/booking.model';
import { BookingDetailModalComponent } from '../booking-detail-modal/booking-detail-modal.component';
import { BookingService } from '../../services/booking.service';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CreateBookingModalComponent } from '../create-booking-modal/create-booking-modal.component';
import moment from 'moment';
import { NeighborhoodService } from '../../services/neighborhood.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SelectAmenityModalComponent } from '../select-amenity-modal/select-amenity-modal.component';

@Component({
  selector: 'app-bookings-calendar',
  templateUrl: './bookings-calendar.component.html',
  styleUrl: './bookings-calendar.component.scss'
})
export class BookingsCalendarComponent implements OnChanges, OnDestroy, OnInit {

  @Input() actives: Booking[] = [];
  @Input() historical: Booking[] = [];
  @Input() loading: boolean = true;
  @Input() neighborhoodId?: string;

  @ViewChild('timeGridScroll') timeGridScroll?: ElementRef<HTMLDivElement>;

  public amenityFilters: AmenityFilter[] = [];
  public anchorDate: moment.Moment = moment();
  public hourHeightPx: number = 56;
  public hourSlots: CalendarHourSlot[] = [];
  public monthWeeks: CalendarWeek[] = [];
  public nowLabel: string = '';
  public nowTopPx: number = 0;
  public periodEventsCount: number = 0;
  public periodTitle: string = '';
  public skeletonSlots: CalendarSkeletonSlot[] = [];
  public stateFilters: StateFilter[] = [
    {
      label: 'Activa',
      state: 'active',
      stateClass: 'is-active',
      visible: true
    },
    {
      label: 'Pasada',
      state: 'past',
      stateClass: 'is-past',
      visible: true
    },
    {
      label: 'Cancelada',
      state: 'cancelled',
      stateClass: 'is-cancelled',
      visible: true
    }
  ];
  public timeDays: CalendarDay[] = [];
  public todayKey: string = '';
  public viewMode: CalendarViewMode = 'week';
  public visibleEventsCount: number = 0;
  public weekdayLabels: string[] = [];

  private allEvents: CalendarEvent[] = [];
  private hiddenAmenityIds: string[] = [];
  private hiddenStates: CalendarEventState[] = [];
  private nowTimerId?: number;

  constructor(
    public bookingService: BookingService,
    private modalService: NgbModal,
    public neighborhoodService: NeighborhoodService
  ) {

    this.buildHourSlots();
    this.buildWeekdayLabels();
    this.buildSkeletonSlots();
  }

  ngOnInit(): void {

    this.nowTimerId = window.setInterval(
      () => this.refreshNowIndicator(),
      60000
    );

    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['actives'] || changes['historical']) {
      this.reload();
      return;
    }

    if (changes['loading']) {
      this.scheduleAutoScroll();
    }
  }

  ngOnDestroy(): void {

    if (this.nowTimerId !== undefined) {
      window.clearInterval(this.nowTimerId);
    }
  }

  get timeZone(): number {
    return this.neighborhoodService.currentNeighborhood?.timeZone ?? 0;
  }

  get filterHidesEverything(): boolean {
    return this.visibleEventsCount === 0 && this.periodEventsCount > 0;
  }

  /*
  ██████╗ ██╗   ██╗██╗██╗     ██████╗
  ██╔══██╗██║   ██║██║██║     ██╔══██╗
  ██████╔╝██║   ██║██║██║     ██║  ██║
  ██╔══██╗██║   ██║██║██║     ██║  ██║
  ██████╔╝╚██████╔╝██║███████╗██████╔╝
  ╚═════╝  ╚═════╝ ╚═╝╚══════╝╚═════╝

  */
  private reload(): void {

    this.buildEvents();
    this.buildAmenityFilters();
    this.buildPeriod();
  }

  private buildEvents(): void {

    const merged: Booking[] = [];
    const seenIds: string[] = [];
    const sources: Booking[] = [...this.actives, ...this.historical];

    for (const booking of sources) {

      if (seenIds.indexOf(booking.bookingId) === -1) {
        seenIds.push(booking.bookingId);
        merged.push(booking);
      }
    }

    const now = moment();

    this.allEvents = merged.map(
      booking => this.toCalendarEvent(
        booking,
        now
      )
    );
  }

  private toCalendarEvent(
    booking: Booking,
    now: moment.Moment
  ): CalendarEvent {

    const startAt = moment(booking.startDate).add(
      this.timeZone,
      'hours'
    );

    let endAt = moment(booking.endDate).add(
      this.timeZone,
      'hours'
    );

    if (!endAt.isAfter(startAt)) {
      endAt = startAt.clone().add(
        1,
        'hours'
      );
    }

    const endOfDay = startAt.clone().endOf('day');

    if (endAt.isAfter(endOfDay)) {
      endAt = endOfDay;
    }

    let state: CalendarEventState = 'active';

    if (!booking.status) {
      state = 'cancelled';

    } else if (endAt.isBefore(now)) {
      state = 'past';
    }

    const color = this.amenityColor(booking.amenityId);
    const paintColor = state === 'cancelled'
      ? calendarCancelledColor
      : color;

    const startMinutes = startAt.hours() * 60 + startAt.minutes();
    const endMinutes = endAt.hours() * 60 + endAt.minutes();

    const durationMinutes = Math.max(
      endMinutes - startMinutes,
      15
    );

    const amenityName = booking.amenity?.name ?? 'Amenidad';
    const startLabel = startAt.format('HH:mm');
    const endLabel = endAt.format('HH:mm');
    const timeRangeLabel = `${startLabel} – ${endLabel}`;
    const stateLabel = this.stateLabel(state);

    return {
      amenityId: booking.amenityId,
      amenityName: amenityName,
      ariaLabel: `${amenityName}, ${timeRangeLabel}, ${stateLabel}`,
      backgroundColor: paintColor.background,
      booking: booking,
      bookingId: booking.bookingId,
      borderColor: paintColor.border,
      color: color,
      createdAtLabel: moment(booking.createdAt).add(
        this.timeZone,
        'hours'
      ).format('DD MMM YYYY HH:mm'),
      createdByName: booking.createdBy?.fullName ?? '',
      dayKey: startAt.format('YYYY-MM-DD'),
      durationMinutes: durationMinutes,
      endAt: endAt,
      endLabel: endLabel,
      endMinutes: endMinutes,
      guests: booking.guests,
      hasEnded: endAt.isBefore(now),
      heightPx: 0,
      houseNumber: booking.houseFlat?.number ?? '',
      isBlocked: booking.isBlocked,
      leftPercent: 0,
      longDateLabel: this.capitalize(startAt.format('dddd, D [de] MMMM [de] YYYY')),
      ownerName: booking.houseFlat?.fullName ?? '',
      startAt: startAt,
      startLabel: startLabel,
      startMinutes: startMinutes,
      state: state,
      stateClass: `is-${state}`,
      stateLabel: stateLabel,
      streetName: booking.streetBuilding?.name ?? '',
      textColor: paintColor.text,
      timeRangeLabel: timeRangeLabel,
      topPx: 0,
      widthPercent: 100
    };
  }

  private stateLabel(state: CalendarEventState): string {

    if (state === 'cancelled') {
      return 'Cancelada';
    }

    if (state === 'past') {
      return 'Pasada';
    }

    return 'Activa';
  }

  private amenityColor(amenityId: string): CalendarColor {
    return amenityColorOf(amenityId);
  }

  /*
  ███████╗██╗██╗  ████████╗██████╗  ██████╗ ███████╗
  ██╔════╝██║██║  ╚══██╔══╝██╔══██╗██╔═══██╗██╔════╝
  █████╗  ██║██║     ██║   ██████╔╝██║   ██║███████╗
  ██╔══╝  ██║██║     ██║   ██╔══██╗██║   ██║╚════██║
  ██║     ██║███████╗██║   ██║  ██║╚██████╔╝███████║
  ╚═╝     ╚═╝╚══════╝╚═╝   ╚═╝  ╚═╝ ╚═════╝ ╚══════╝

  */
  private buildAmenityFilters(): void {

    const filters: AmenityFilter[] = [];

    for (const event of this.allEvents) {

      const found = filters.find(
        filter => filter.amenityId === event.amenityId
      );

      if (!found) {
        filters.push({
          amenityId: event.amenityId,
          color: event.color,
          name: event.amenityName,
          visible: this.hiddenAmenityIds.indexOf(event.amenityId) === -1
        });
      }
    }

    filters.sort(this.compareAmenityFilters);
    this.amenityFilters = filters;
  }

  private compareAmenityFilters(
    firstFilter: AmenityFilter,
    secondFilter: AmenityFilter
  ): number {
    return firstFilter.name.localeCompare(secondFilter.name);
  }

  toggleAmenity(amenityFilter: AmenityFilter): void {

    amenityFilter.visible = !amenityFilter.visible;

    if (amenityFilter.visible) {
      this.hiddenAmenityIds = this.hiddenAmenityIds.filter(
        amenityId => amenityId !== amenityFilter.amenityId
      );

    } else {
      this.hiddenAmenityIds.push(amenityFilter.amenityId);
    }

    this.buildPeriod();
  }

  toggleState(stateFilter: StateFilter): void {

    stateFilter.visible = !stateFilter.visible;

    if (stateFilter.visible) {
      this.hiddenStates = this.hiddenStates.filter(
        state => state !== stateFilter.state
      );

    } else {
      this.hiddenStates.push(stateFilter.state);
    }

    this.buildPeriod();
  }

  showAllAmenities(): void {

    this.hiddenAmenityIds = [];

    for (const amenityFilter of this.amenityFilters) {
      amenityFilter.visible = true;
    }

    this.buildPeriod();
  }

  hideAllAmenities(): void {

    this.hiddenAmenityIds = this.amenityFilters.map(
      amenityFilter => amenityFilter.amenityId
    );

    for (const amenityFilter of this.amenityFilters) {
      amenityFilter.visible = false;
    }

    this.buildPeriod();
  }

  private eventsOfDay(dateKey: string): CalendarEvent[] {

    const events = this.allEvents.filter(
      event => event.dayKey === dateKey
        && this.hiddenAmenityIds.indexOf(event.amenityId) === -1
        && this.hiddenStates.indexOf(event.state) === -1
    );

    return events.sort(this.compareEvents);
  }

  private countEventsOfDay(dateKey: string): number {

    return this.allEvents.filter(
      event => event.dayKey === dateKey
    ).length;
  }

  private compareEvents(
    firstEvent: CalendarEvent,
    secondEvent: CalendarEvent
  ): number {

    if (firstEvent.startMinutes !== secondEvent.startMinutes) {
      return firstEvent.startMinutes - secondEvent.startMinutes;
    }

    return secondEvent.durationMinutes - firstEvent.durationMinutes;
  }

  /*
  ██████╗ ███████╗██████╗ ██╗ ██████╗ ██████╗  ██████╗
  ██╔══██╗██╔════╝██╔══██╗██║██╔═══██╗██╔══██╗██╔═══██╗
  ██████╔╝█████╗  ██████╔╝██║██║   ██║██║  ██║██║   ██║
  ██╔═══╝ ██╔══╝  ██╔══██╗██║██║   ██║██║  ██║██║   ██║
  ██║     ███████╗██║  ██║██║╚██████╔╝██████╔╝╚██████╔╝
  ╚═╝     ╚══════╝╚═╝  ╚═╝╚═╝ ╚═════╝ ╚═════╝  ╚═════╝

  */
  private buildPeriod(): void {

    this.refreshNowIndicator();
    this.periodTitle = this.buildPeriodTitle();

    if (this.viewMode === 'month') {
      this.timeDays = [];
      this.buildMonthGrid();

    } else {
      this.monthWeeks = [];
      this.buildTimeGrid();
    }

    this.scheduleAutoScroll();
  }

  private buildPeriodTitle(): string {

    if (this.viewMode === 'month') {
      return this.capitalize(this.anchorDate.format('MMMM YYYY'));
    }

    if (this.viewMode === 'day') {
      return this.capitalize(this.anchorDate.format('dddd, D [de] MMMM [de] YYYY'));
    }

    const weekStart = this.anchorDate.clone().startOf('isoWeek');
    const weekEnd = this.anchorDate.clone().endOf('isoWeek');

    if (weekStart.year() !== weekEnd.year()) {
      return `${weekStart.date()} ${this.shortMonth(weekStart)} ${weekStart.year()} – ${weekEnd.date()} ${this.shortMonth(weekEnd)} ${weekEnd.year()}`;
    }

    if (weekStart.month() !== weekEnd.month()) {
      return `${weekStart.date()} ${this.shortMonth(weekStart)} – ${weekEnd.date()} ${this.shortMonth(weekEnd)} ${weekEnd.year()}`;
    }

    return `${weekStart.date()} – ${weekEnd.date()} ${this.shortMonth(weekEnd)} ${weekEnd.year()}`;
  }

  private buildMonthGrid(): void {

    const gridStart = this.anchorDate.clone().startOf('month').startOf('isoWeek');
    const gridEnd = this.anchorDate.clone().endOf('month').endOf('isoWeek');
    const anchorMonth = this.anchorDate.month();
    const cursor = gridStart.clone();

    const weeks: CalendarWeek[] = [];
    let days: CalendarDay[] = [];
    let visibleCounter = 0;
    let periodCounter = 0;

    while (cursor.isSameOrBefore(
      gridEnd,
      'day'
    )) {

      const dateKey = cursor.format('YYYY-MM-DD');
      const dayEvents = this.eventsOfDay(dateKey);

      visibleCounter = visibleCounter + dayEvents.length;
      periodCounter = periodCounter + this.countEventsOfDay(dateKey);

      days.push({
        dateKey: dateKey,
        dayNumber: cursor.date(),
        events: dayEvents,
        isCurrentMonth: cursor.month() === anchorMonth,
        isToday: dateKey === this.todayKey,
        longLabel: this.capitalize(cursor.format('dddd D [de] MMMM')),
        moreCount: dayEvents.length > 3
          ? dayEvents.length - 3
          : 0,
        previewEvents: dayEvents.slice(
          0,
          3
        ),
        weekdayLabel: this.capitalize(this.stripDots(cursor.format('ddd')))
      });

      if (days.length === 7) {
        weeks.push({
          days: days,
          weekKey: days[0].dateKey
        });
        days = [];
      }

      cursor.add(
        1,
        'day'
      );
    }

    this.monthWeeks = weeks;
    this.periodEventsCount = periodCounter;
    this.visibleEventsCount = visibleCounter;
  }

  private buildTimeGrid(): void {

    const dayCount = this.viewMode === 'day'
      ? 1
      : 7;

    const cursor = this.viewMode === 'day'
      ? this.anchorDate.clone().startOf('day')
      : this.anchorDate.clone().startOf('isoWeek');

    const days: CalendarDay[] = [];
    let visibleCounter = 0;
    let periodCounter = 0;

    for (let index = 0; index < dayCount; index++) {

      const dateKey = cursor.format('YYYY-MM-DD');
      const dayEvents = this.eventsOfDay(dateKey);

      this.layoutDayEvents(dayEvents);
      visibleCounter = visibleCounter + dayEvents.length;
      periodCounter = periodCounter + this.countEventsOfDay(dateKey);

      days.push({
        dateKey: dateKey,
        dayNumber: cursor.date(),
        events: dayEvents,
        isCurrentMonth: true,
        isToday: dateKey === this.todayKey,
        longLabel: this.capitalize(cursor.format('dddd D [de] MMMM')),
        moreCount: 0,
        previewEvents: [],
        weekdayLabel: this.capitalize(this.stripDots(cursor.format('ddd')))
      });

      cursor.add(
        1,
        'day'
      );
    }

    this.timeDays = days;
    this.periodEventsCount = periodCounter;
    this.visibleEventsCount = visibleCounter;
  }

  /*
  ██╗      █████╗ ██╗   ██╗ ██████╗ ██╗   ██╗████████╗
  ██║     ██╔══██╗╚██╗ ██╔╝██╔═══██╗██║   ██║╚══██╔══╝
  ██║     ███████║ ╚████╔╝ ██║   ██║██║   ██║   ██║
  ██║     ██╔══██║  ╚██╔╝  ██║   ██║██║   ██║   ██║
  ███████╗██║  ██║   ██║   ╚██████╔╝╚██████╔╝   ██║
  ╚══════╝╚═╝  ╚═╝   ╚═╝    ╚═════╝  ╚═════╝    ╚═╝

  */
  private layoutDayEvents(events: CalendarEvent[]): void {

    let cluster: CalendarEvent[] = [];
    let clusterEnd = -1;

    for (const event of events) {

      if (cluster.length > 0 && event.startMinutes >= clusterEnd) {
        this.layoutCluster(cluster);
        cluster = [];
        clusterEnd = -1;
      }

      cluster.push(event);

      clusterEnd = Math.max(
        clusterEnd,
        event.endMinutes
      );
    }

    if (cluster.length > 0) {
      this.layoutCluster(cluster);
    }
  }

  private layoutCluster(cluster: CalendarEvent[]): void {

    const columns: CalendarEvent[][] = [];

    for (const event of cluster) {

      let placed = false;

      for (const column of columns) {

        const lastEvent = column[column.length - 1];

        if (lastEvent.endMinutes <= event.startMinutes) {
          column.push(event);
          placed = true;
          break;
        }
      }

      if (!placed) {
        columns.push([event]);
      }
    }

    const columnWidth = 100 / columns.length;

    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {

      for (const event of columns[columnIndex]) {

        event.topPx = event.startMinutes * this.hourHeightPx / 60;

        event.heightPx = Math.max(
          event.durationMinutes * this.hourHeightPx / 60,
          24
        );

        event.leftPercent = columnIndex * columnWidth;
        event.widthPercent = columnWidth;
      }
    }
  }

  /*
  ███╗   ██╗ █████╗ ██╗   ██╗
  ████╗  ██║██╔══██╗██║   ██║
  ██╔██╗ ██║███████║██║   ██║
  ██║╚██╗██║██╔══██║╚██╗ ██╔╝
  ██║ ╚████║██║  ██║ ╚████╔╝
  ╚═╝  ╚═══╝╚═╝  ╚═╝  ╚═══╝

  */
  goToday(): void {
    this.anchorDate = moment();
    this.buildPeriod();
  }

  goPrevious(): void {
    this.shiftPeriod(-1);
  }

  goNext(): void {
    this.shiftPeriod(1);
  }

  private shiftPeriod(direction: number): void {

    let unit: moment.unitOfTime.DurationConstructor = 'day';

    if (this.viewMode === 'month') {
      unit = 'month';
    }

    if (this.viewMode === 'week') {
      unit = 'week';
    }

    this.anchorDate = this.anchorDate.clone().add(
      direction,
      unit
    );

    this.buildPeriod();
  }

  setViewMode(viewMode: CalendarViewMode): void {
    this.viewMode = viewMode;
    this.buildPeriod();
  }

  openDay(dateKey: string): void {
    this.anchorDate = moment(dateKey);
    this.viewMode = 'day';
    this.buildPeriod();
  }

  refresh(): void {

    if (this.neighborhoodId) {
      this.bookingService.getAll(this.neighborhoodId);
    }
  }

  /*
   ██████╗ ██████╗ ███████╗███╗   ██╗
  ██╔═══██╗██╔══██╗██╔════╝████╗  ██║
  ██║   ██║██████╔╝█████╗  ██╔██╗ ██║
  ██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║
  ╚██████╔╝██║     ███████╗██║ ╚████║
   ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝

  */
  openAmenitySelector(): void {

    const modalRef = this.modalService.open(
      SelectAmenityModalComponent,
      {
        size: 'lg'
      }
    );

    modalRef.componentInstance.neighborhoodId = this.neighborhoodId;

    modalRef.closed.subscribe(
      (amenity: Amenity) => {

        if (amenity) {
          this.createBooking(amenity);
        }
      }
    );
  }

  createBooking(amenity: Amenity): void {

    const modalRef = this.modalService.open(CreateBookingModalComponent);

    modalRef.componentInstance.neighborhoodId = this.neighborhoodId;
    modalRef.componentInstance.amenity = amenity;

    modalRef.closed.subscribe(
      () => this.refresh()
    );
  }

  openEvent(event: CalendarEvent): void {

    const modalRef = this.modalService.open(
      BookingDetailModalComponent,
      {
        size: 'lg'
      }
    );

    modalRef.componentInstance.event = event;
  }

  /*
  ██╗  ██╗███████╗██╗     ██████╗ ███████╗██████╗ ███████╗
  ██║  ██║██╔════╝██║     ██╔══██╗██╔════╝██╔══██╗██╔════╝
  ███████║█████╗  ██║     ██████╔╝█████╗  ██████╔╝███████╗
  ██╔══██║██╔══╝  ██║     ██╔═══╝ ██╔══╝  ██╔══██╗╚════██║
  ██║  ██║███████╗███████╗██║     ███████╗██║  ██║███████║
  ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝╚══════╝

  */
  private buildHourSlots(): void {

    for (let hour = 0; hour < 24; hour++) {

      const label = `${hour}`.padStart(
        2,
        '0'
      );

      this.hourSlots.push({
        hour: hour,
        label: `${label}:00`
      });
    }
  }

  private buildWeekdayLabels(): void {

    const cursor = moment().startOf('isoWeek');

    for (let index = 0; index < 7; index++) {

      this.weekdayLabels.push(
        this.capitalize(this.stripDots(cursor.format('ddd')))
      );

      cursor.add(
        1,
        'day'
      );
    }
  }

  private buildSkeletonSlots(): void {

    const startMinutes: number[] = [480, 630, 840];
    const durations: number[] = [60, 90, 45];

    for (let index = 0; index < startMinutes.length; index++) {

      this.skeletonSlots.push({
        heightPx: durations[index] * this.hourHeightPx / 60,
        key: `skeleton-${index}`,
        topPx: startMinutes[index] * this.hourHeightPx / 60
      });
    }
  }

  private refreshNowIndicator(): void {

    const now = moment();

    this.todayKey = now.format('YYYY-MM-DD');
    this.nowLabel = now.format('HH:mm');
    this.nowTopPx = (now.hours() * 60 + now.minutes()) * this.hourHeightPx / 60;
  }

  private scheduleAutoScroll(): void {

    if (this.viewMode === 'month') {
      return;
    }

    window.setTimeout(
      () => this.applyAutoScroll(),
      0
    );
  }

  private applyAutoScroll(): void {

    const container = this.timeGridScroll?.nativeElement;

    if (!container) {
      return;
    }

    let firstMinutes: number | undefined;

    for (const day of this.timeDays) {

      for (const event of day.events) {

        if (firstMinutes === undefined || event.startMinutes < firstMinutes) {
          firstMinutes = event.startMinutes;
        }
      }
    }

    const targetMinutes = firstMinutes ?? 420;

    container.scrollTop = Math.max(
      0,
      targetMinutes * this.hourHeightPx / 60 - 24
    );
  }

  private capitalize(value: string): string {

    return value.length > 0
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value;
  }

  private stripDots(value: string): string {

    return value.replace(
      /\./g,
      ''
    );
  }

  private shortMonth(value: moment.Moment): string {
    return this.stripDots(value.format('MMM'));
  }

}
